import type { Entries } from "type-fest";
import { z } from "zod";
import type * as CookieConsent from "vanilla-cookieconsent";

import { Tk } from "../i18n";

import { Category } from "./category";
import { SERVICE_LABEL_TK } from "./types";

const zService = z.enum([
  "ad_personalization",
  "ad_storage",
  "ad_user_data",
  "analytics_storage",
  "functionality_storage",
  "personalization_storage",
  "security_storage",
]);
const Service = zService.Enum;
type Service = z.infer<typeof zService>;

type Categories = Partial<Record<Category, CookieConsent.Category>>;

const CATEGORIES: Categories = {
  necessary: {
    enabled: true,
    readOnly: true,
  },
  analytics: {
    services: {
      analytics_storage: {
        [SERVICE_LABEL_TK]: Tk.CONSENT_PREFERENCES_MODAL_SERVICE_ANALYTICS_STORAGE,
      },
    },
  },
  advertisement: {
    services: {
      ad_storage: {
        [SERVICE_LABEL_TK]: Tk.CONSENT_PREFERENCES_MODAL_SERVICE_AD_STORAGE,
      },
      ad_user_data: {
        [SERVICE_LABEL_TK]: Tk.CONSENT_PREFERENCES_MODAL_SERVICE_AD_USER_DATA,
      },
      ad_personalization: {
        [SERVICE_LABEL_TK]: Tk.CONSENT_PREFERENCES_MODAL_SERVICE_AD_PERSONALIZATION,
      },
    },
  },
  functionality: {
    enabled: true,
    services: {
      functionality_storage: {
        [SERVICE_LABEL_TK]: Tk.CONSENT_PREFERENCES_MODAL_SERVICE_FUNCTIONALITY_STORAGE,
      },
      personalization_storage: {
        [SERVICE_LABEL_TK]: Tk.CONSENT_PREFERENCES_MODAL_SERVICE_PERSONALIZATION_STORAGE,
      },
    },
  },
  security: {
    enabled: true,
    services: {
      security_storage: {
        [SERVICE_LABEL_TK]: Tk.CONSENT_PREFERENCES_MODAL_SERVICE_SECURITY_STORAGE,
      },
    },
  },
};

type CategoryServices = Partial<Record<Category, Partial<Record<Service, CookieConsent.Service>>>>;

const CATEGORY_SERVICES: CategoryServices = {
  ...Object.fromEntries(Object.keys(Category).map((category) => [category, {}])),
  ...Object.fromEntries(
    Object.entries(CATEGORIES).map(([category, { services = {} }]) => [category, services]),
  ),
};

const CATEGORY_BY_SERVICE = Object.fromEntries(
  (Object.entries(CATEGORY_SERVICES) as Entries<typeof CATEGORY_SERVICES>).flatMap(
    ([category, services = {}]) =>
      Object.keys(services).map((service) => [service, category] as const),
  ),
);

export { CATEGORY_BY_SERVICE, CATEGORY_SERVICES, Service, zService, CATEGORIES };
export type { Categories, CategoryServices };
